"use client";

import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { cn } from '@/lib/utils';
import { FileText, PackageSearch, PenTool, Factory, ShieldCheck, Ship } from 'lucide-react';

interface WorkflowStep {
  step: string;
  title: string;
  desc: string;
}

interface WorkflowSectionProps {
  workflow: WorkflowStep[];
}

export function WorkflowSection({ workflow }: WorkflowSectionProps) {
  const [activeStep, setActiveStep] = useState(0);
  const stepRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const handleScroll = () => {
      // Find which step is currently closest to the vertical center of the viewport
      const viewportCenter = window.innerHeight / 2;
      let closestStep = 0;
      let minDistance = Infinity;

      stepRefs.current.forEach((ref, index) => {
        if (!ref) return;
        const rect = ref.getBoundingClientRect();
        // Calculate the center of the element
        const elementCenter = rect.top + rect.height / 2;
        const distance = Math.abs(viewportCenter - elementCenter);
        
        if (distance < minDistance) {
          minDistance = distance;
          closestStep = index;
        }
      });

      setActiveStep(closestStep);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    // Trigger once on mount
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Icons matching the 6 steps of the workflow
  const icons = [FileText, PackageSearch, PenTool, Factory, ShieldCheck, Ship];
  
  // Images for each step
  const stepImages = [
    "/workflow-inquiry.webp",
    "/workflow-samples.webp",
    "/workflow-quotation.webp",
    "/workflow-production.webp",
    "/workflow-inspection.webp",
    "/workflow-dispatch.webp",
  ];

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
      <div className="max-w-2xl mb-14 text-center mx-auto lg:mx-0 lg:text-left">
        <p className="text-xs text-brand-accent font-bold tracking-[0.2em] uppercase mb-3">05 — How We Work</p>
        <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight mb-4">
          From Inquiry to Dispatch
        </h2>
        <p className="text-muted-foreground leading-relaxed text-lg">
          No confusion or delays. Just fast and reliable manufacturing.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 relative items-start">
        {/* Left Side: Sticky Image */}
        <div className="hidden lg:block sticky top-32 w-full h-[600px] rounded-3xl overflow-hidden shadow-2xl border border-border">
          {stepImages.map((src, idx) => (
            <Image
              key={src}
              src={src}
              alt={`Step ${idx + 1}`}
              fill
              className={cn(
                "object-cover transition-opacity duration-700 ease-in-out",
                activeStep === idx ? "opacity-100" : "opacity-0"
              )}
              priority={idx === 0}
            />
          ))}
          <div className="absolute inset-0 bg-gradient-to-t from-background/20 to-transparent" />
        </div>

        {/* Right Side: Scrollable Steps */}
        <div className="relative py-10 lg:py-[10vh]">
          {/* Continuous vertical line track */}
          <div className="absolute left-0 top-0 bottom-0 w-1 bg-border rounded-full hidden sm:block" />
          
          {/* Active portion of the vertical line */}
          <div 
            className="absolute left-0 top-0 w-1 bg-brand-accent rounded-full transition-all duration-700 ease-out z-0 hidden sm:block" 
            style={{ 
              height: activeStep === workflow.length - 1 ? '100%' : `${((activeStep + 0.5) / workflow.length) * 100}%` 
            }} 
          />

          <div className="space-y-12 lg:space-y-24 relative z-10 pl-0 sm:pl-12">
            {workflow.map((w, index) => {
              const Icon = icons[index % icons.length];
              const isActive = index === activeStep;

              return (
                <div 
                  key={w.step} 
                  ref={(el) => { stepRefs.current[index] = el; }}
                  className={cn(
                    "flex gap-6 transition-all duration-500",
                    isActive ? "opacity-100 sm:translate-x-0" : "opacity-50 sm:translate-x-2"
                  )}
                >
                  {/* Icon Node */}
                  <div className="relative shrink-0 flex items-start hidden sm:flex pt-1">
                    <div 
                      className={cn(
                        "w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-500 z-10",
                        isActive 
                          ? "bg-brand-accent text-white shadow-lg shadow-brand-accent/30 scale-105" 
                          : "bg-muted text-muted-foreground"
                      )}
                    >
                      <Icon className="w-6 h-6" />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 bg-card sm:bg-transparent p-6 sm:p-0 rounded-2xl sm:rounded-none border sm:border-0 border-border shadow-sm sm:shadow-none">
                    <div className="flex items-center gap-3 sm:hidden mb-4">
                      <div className={cn(
                        "w-12 h-12 rounded-xl flex items-center justify-center",
                        isActive ? "bg-brand-accent text-white" : "bg-muted text-muted-foreground"
                      )}>
                         <Icon className="w-5 h-5" />
                      </div>
                    </div>
                    <h3 className="font-display font-bold text-xl md:text-2xl tracking-tight mb-2 text-foreground">
                      {w.title}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed text-sm md:text-base">
                      {w.desc}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
